import { useCallback } from "react"
import { useMutation } from "urql"

import type { ProductTestFormPayload } from "../types"

import { useUpdateProductTestDocuments } from "./useUpdateProductTestDocuments"

import type {
  CreateProductTestMutation,
  ProductTestCreateFailure,
  ProductTestCreateInput,
  ProductTestCreateSuccess,
  ProductTestMutationCreateArgs,
} from "@/graphql/codegen/graphql"
import { CreateProductTestDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateProductTestReturn {
  createProductTest: (payload: ProductTestFormPayload, productId: number) => Promise<boolean>
  isCreating: boolean
}

export const useCreateProductTest = (): UseCreateProductTestReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { updateDocuments, isUpdatingDocuments } = useUpdateProductTestDocuments()
  const [{ fetching }, executeMutation] = useMutation<CreateProductTestMutation, ProductTestMutationCreateArgs>(
    CreateProductTestDocument
  )

  const createProductTest = useCallback(
    async (payload: ProductTestFormPayload, productId: number): Promise<boolean> => {
      const input = { ...payload.productTest, productId } as ProductTestCreateInput
      const { data, error } = await executeMutation({ input })
      const output = data?.productTest.create

      if (error || output?.__typename === "ProductTestCreateFailure") {
        const message = error?.message ?? (output as ProductTestCreateFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      const { productTestId } = (output as ProductTestCreateSuccess).productTest

      await updateDocuments({ productTestId, documentsToUpload: payload.documentsToUpload })
      notify({ type: NotificationType.Success, message: "Product test data successfully created." })

      return true
    },
    [executeMutation, updateDocuments, notify]
  )

  return { createProductTest, isCreating: fetching || isUpdatingDocuments }
}
